<script setup>
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'
import { HaiType, haiTypeColor, haiTypeIcon } from '@/enums/quality/hai-type'
import BundleComplianceGraph from '@/views/quality/infection-control/dashboard/BundleComplianceGraph.vue'
import DeviceUtilizationGraph from '@/views/quality/infection-control/dashboard/DeviceUtilizationGraph.vue'
import HandHygieneGraph from '@/views/quality/infection-control/dashboard/HandHygieneGraph.vue'
import HaiRateByLocationGraph from '@/views/quality/infection-control/dashboard/HaiRateByLocationGraph.vue'
import HaiRateTrendGraph from '@/views/quality/infection-control/dashboard/HaiRateTrendGraph.vue'
import MdroTrendGraph from '@/views/quality/infection-control/dashboard/MdroTrendGraph.vue'
import MonthlySummaryCard from '@/views/quality/infection-control/dashboard/MonthlySummaryCard.vue'
import OrganismDistributionGraph from '@/views/quality/infection-control/dashboard/OrganismDistributionGraph.vue'

definePage({
  meta: {
    subject: 'quality.infection_control.menu',
    action: 'dashboard',
  },
})

const now = new Date()

const selectedMonth = ref(now.getMonth() + 1)
const selectedYear = ref(now.getFullYear())

const monthOptions = [
  { title: 'January', value: 1 },
  { title: 'February', value: 2 },
  { title: 'March', value: 3 },
  { title: 'April', value: 4 },
  { title: 'May', value: 5 },
  { title: 'June', value: 6 },
  { title: 'July', value: 7 },
  { title: 'August', value: 8 },
  { title: 'September', value: 9 },
  { title: 'October', value: 10 },
  { title: 'November', value: 11 },
  { title: 'December', value: 12 },
]

const yearOptions = computed(() => {
  const currentYear = now.getFullYear()
  const years = []

  for (let y = currentYear - 4; y <= currentYear; y++) {
    years.push({ title: String(y), value: y })
  }

  return years
})

const {
  isFetching: isSummaryFetching,
  data: summaryData,
} = useApi(createUrl('/dashboard/infection-control/monthly-summary', {
  query: {
    month: selectedMonth,
    year: selectedYear,
  },
}))

const haiSummaryCards = computed(() => {
  const summary = summaryData.value ?? {}

  return [
    {
      key: HaiType.CLABSI,
      title: 'CLABSI',
      rate: summary.clabsi_rate ?? null,
      benchmark: summary.clabsi_benchmark ?? null,
      status: summary.clabsi_status ?? 'NORMAL',
      previousRate: summary.clabsi_previous_rate ?? null,
      unit: '/1,000 CL days',
      icon: haiTypeIcon(HaiType.CLABSI),
      color: haiTypeColor(HaiType.CLABSI),
    },
    {
      key: HaiType.CAUTI,
      title: 'CAUTI',
      rate: summary.cauti_rate ?? null,
      benchmark: summary.cauti_benchmark ?? null,
      status: summary.cauti_status ?? 'NORMAL',
      previousRate: summary.cauti_previous_rate ?? null,
      unit: '/1,000 UC days',
      icon: haiTypeIcon(HaiType.CAUTI),
      color: haiTypeColor(HaiType.CAUTI),
    },
    {
      key: HaiType.VAP,
      title: 'VAP',
      rate: summary.vap_rate ?? null,
      benchmark: summary.vap_benchmark ?? null,
      status: summary.vap_status ?? 'NORMAL',
      previousRate: summary.vap_previous_rate ?? null,
      unit: '/1,000 vent days',
      icon: haiTypeIcon(HaiType.VAP),
      color: haiTypeColor(HaiType.VAP),
    },
    {
      key: HaiType.SSI,
      title: 'SSI',
      rate: summary.ssi_rate ?? null,
      benchmark: summary.ssi_benchmark ?? null,
      status: summary.ssi_status ?? 'NORMAL',
      previousRate: summary.ssi_previous_rate ?? null,
      unit: '/100 surgeries',
      icon: haiTypeIcon(HaiType.SSI),
      color: haiTypeColor(HaiType.SSI),
    },
  ]
})
</script>

<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h4 class="text-h4 font-weight-medium">
          Infection Control Dashboard
        </h4>
        <p class="text-body-2 text-medium-emphasis mt-1">
          HAI surveillance rates, organism distribution and device utilization
        </p>
      </div>

      <!-- Filters -->
      <div class="d-flex align-center gap-3">
        <AppSelect
          v-model="selectedMonth"
          label="Month"
          :items="monthOptions"
          item-title="title"
          item-value="value"
          style="min-width: 140px;"
        />
        <AppSelect
          v-model="selectedYear"
          label="Year"
          :items="yearOptions"
          item-title="title"
          item-value="value"
          style="min-width: 100px;"
        />
      </div>
    </div>

    <!-- Summary Cards -->
    <VRow class="mb-6">
      <VCol
        v-for="card in haiSummaryCards"
        :key="card.key"
        cols="12"
        sm="6"
        lg="3"
      >
        <VSkeletonLoader
          v-if="isSummaryFetching"
          type="card"
          width="100%"
        />
        <MonthlySummaryCard
          v-else
          :title="card.title"
          :rate="card.rate"
          :benchmark="card.benchmark"
          :status="card.status"
          :previous-rate="card.previousRate"
          :unit="card.unit"
          :icon="card.icon"
          :color="card.color"
        />
      </VCol>
    </VRow>

    <!-- Charts Row 1 -->
    <VRow class="mb-6">
      <VCol
        cols="12"
        md="6"
      >
        <HaiRateTrendGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <HaiRateByLocationGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
    </VRow>

    <!-- Charts Row 2 -->
    <VRow class="mb-6">
      <VCol
        cols="12"
        md="6"
      >
        <OrganismDistributionGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <MdroTrendGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
    </VRow>

    <!-- Full Width: Device Utilization -->
    <VRow class="mb-6">
      <VCol cols="12">
        <DeviceUtilizationGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
    </VRow>

    <!-- Hand Hygiene & Bundle Compliance -->
    <VRow class="mt-6">
      <VCol
        cols="12"
        md="6"
      >
        <HandHygieneGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <BundleComplianceGraph
          :month="selectedMonth"
          :year="selectedYear"
        />
      </VCol>
    </VRow>
  </div>
</template>
